import {css} from 'styled-components';
import {tokensForThemeProvider} from '@forecast-it/design-system';

// Shared design tokens and layout constants for the ai-chat panel.
export const chatFont = "'ABC Whyte', sans-serif";

export const chatBodyFont = css`
	font-family: ${chatFont};
	font-weight: 400;
`;

export const chatHeadingFont = css`
	font-family: ${chatFont};
	font-weight: 500;
`;

// ── Action-card key/value type (TNSR-450) ────────────────────────────────────
//
// Pinned to the Sky token set at module scope, the same way the rest of the
// agent drawer does it (DrawerHeader, DrawerFooter, KebabMenu, PromptRow,
// PromptDropdown all destructure tokensForThemeProvider.sky.sys). That keeps
// these values sourced from the design system rather than hand-copied into `D`,
// while leaving Sage rendering exactly as it is today — the drawer is Sky-only
// throughout, and theming this card alone would make it disagree with its own
// neighbours. Moving the whole drawer onto the live theme is its own ticket.
//
// Shared by KeyValueForm (editable rows) and KeyValueTable (read-only rows) so
// the two row renderers cannot drift apart.
const sky = tokensForThemeProvider.sky.sys;

// Figma "Action Box" key column — sky/sys/font/detail/2 + text/content/auxiliary,
// upper-cased. text-transform (not toUpperCase) because the backend sends mixed
// casing — 'ASSIGNEE' from kv, 'Source project' from fields[] — and CSS
// normalises both without changing the accessible name.
export const kvKeyType = css`
	${sky.font.detail['2']};
	color: ${sky.color.text.content.auxiliary};
	text-transform: uppercase;
`;

// Figma "Action Box" value column — sky/sys/font/body/2 + text/content/main.
export const kvValueType = css`
	${sky.font.body['2']};
	color: ${sky.color.text.content.main};
`;

// Every key/value row in the Figma frame is exactly 44px tall, regardless of
// content type — empty, em-dash, link, input and date-range states all line up.
// min-height (not height) so a row can still grow when wrapped text exceeds it.
export const KV_ROW_MIN_HEIGHT = 44;

// Re-exported so the action-card components share one pinned token set.
export const skyTokens = sky;

export const D = {
	bg: '#ffffff',
	surface: '#f8f7fb',
	surface2: '#f0ecfa',
	border: '#e9e9e9',
	borderFocus: '#6E0FEB',
	textPrimary: '#2e2541',
	textSecondary: '#535353',
	textMuted: '#8c8c8c',
	textDisabled: '#acacb9',
	accent: '#392664',
	accentMid: '#6E0FEB',
	accentHover: '#f0ecfa',
	userBubble: tokensForThemeProvider.sky.sys.color.background.primary['2'],
	userBubbleBorder: 'transparent',
	assistantBubble: '#fcfbfd',
	assistantBubbleBorder: '#d3d3df',
	inputBg: '#ffffff',
	inputBorder: '#d3d3df',
	stepDoneBg: '#f0fdf4',
	stepDoneBorder: '#86efac',
	stepDoneText: '#15803d',
	stepActiveBg: '#f0ecfa',
	stepActiveBorder: '#c4b5fd',
	stepActiveText: '#6d28d9',
	stepIdleBg: '#f9fafb',
	stepIdleBorder: '#e5e7eb',
	stepIdleText: '#6b7280',
	stepIdleDot: '#d1d5db',
	errorBg: '#fef2f2',
	errorBorder: '#fca5a5',
	errorText: '#e23f3f',
	drawerShadow: '0 2px 4px 0 rgba(35, 35, 52, 0.12)',
	promptDropdownShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.12)',
	promptRowSelectedBg: '#ebe8f5',
	// sky/sys colour overrides used by AgentDrawer and newer ai-chat components
	v2: {
		textPrimary: '#232334',
		textSecondary: '#6b6d7b',
		borderEnabled: '#6b6d7b',
		accent: '#0004db',
		surfaceHover: '#f5f3f9',
		iconInactive: '#9898a8',
		iconFillDisabled: '#d3d3df',
		btnInactiveBg: '#e8e8f0',
		statusGreen: '#148f6a',
		statusAmber: '#e57700',
		statusRed: '#db0900',
	},
};

export const TRUNCATE_AT = 1200;
export const PREVIEW_CHARS = 800;
export const MIN_WIDTH = 340;
export const MAX_WIDTH = 860;
export const DEFAULT_WIDTH = 560;
export const CARD_BOTTOM = 80;
